import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { func } from 'prop-types';
import {
  TextFieldLabel, Button, Select, Spinner, TextField,
} from 'ui-component-library';
import * as yup from 'yup'; // for everything

import {
  FormHeader,
  TopRowContainer,
  ProblemInputContainer,
  SubmitButtonContainer,
  EmptyStateContainer,
  LoadingContainer,
} from './action-resolve-problem.style';
import {
  showSuccessMessage, showErrorMessage, validate, logger,
} from '../../../../../../utils/common';
import { formatErrorResponse } from '../../../../../../utils/mappers';
import { resolveLoadProblem } from '../../../../../../utils/api/load';
import CommentBox from '../../../comments-box';
import loadShape from '../../../../../../shapes/load';
import { COMMENTS_CHARACTER_LIMIT } from '../../../../../../constants';
import { getResolvedProblemEmailBody, getLoadIdHyperlink } from '../../../modals/email-modal/email-templates';
import { getHtmlFromTxt, removeFirstLine } from '../../../../../../utils/formatters';

// Validation Schema
export const schema = yup.object().shape({
  problem: yup.string().required(),
  note: yup.string().required()
    .max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less that ${COMMENTS_CHARACTER_LIMIT}`),
  lumperCode: yup.string().when('problem', {
    is: (val) => (val === 'Lumper Reimbursement'),
    then: yup.string().min(1).required(),
    otherwise: yup.string().optional().nullable(),
  }),
});

/**
 * Resolved Problem action form
 * @param {object} params - Contains props for this component
 * @param {object} load - Load data to display
 * @param {function} params.refetchLoad - Function to reload load summary in parent
 * @return {element} - The resolved problem form
 */
const ResolveProblemAction = ({ load, refetchLoad }) => {
  const user = useSelector((store) => store.oidc.user);

  // State hooks for form values
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lumperCode, setLumperCode] = useState('');

  /**
   * Handles creating a Select option based of a string
   * @param {string} option - String to create an option from
   * @return {object} - An option object that can be used in a HTML select
   */
  const createOption = (option) => ({ label: option, value: option });

  /**
  * Checks if the form is valid
  * @return {boolean}
  */
  const checkValid = () => {
    const payload = {
      problem: selectedProblem?.value,
      note: comments,
      lumperCode,
    };
    const valid = validate(payload, schema);
    return valid;
  };

  /**
   * Function to clear the current state of the form
   * @return {void}
   */
  const resetState = () => {
    setSelectedProblem(null);
    setComments(null);
  };

  // Set initial value of selectedProblem
  useEffect(() => {
    resetState();
    if (load?.problems?.length === 1) {
      setSelectedProblem(createOption(load.problems[0]));
    }
  }, [load]);

  /**
   * Handles when the selected problem has changed
   * @param {object} event - Change event
   * @return {void}
   */
  const handleSelectedProblemChanged = (event) => {
    setSelectedProblem(event);
  };

  /**
   * Handles when comments change
   * @param {object} event - Change event
   * @return {void}
   */
  const handleCommentsChanged = (event) => {
    setComments(event.target.value);
  };

  /**
   * Handles submitting the resolve problem form
   * @return {void}
   */
  const handleSubmit = () => {
    const valid = checkValid();
    if (valid) {
      const createEmailBody = getLoadIdHyperlink(load.loadId, load.billOfLadingNumber)
      // eslint-disable-next-line max-len
      + getHtmlFromTxt(removeFirstLine(getResolvedProblemEmailBody(load, selectedProblem?.value, comments, user.username)));
      const payload = {
        problem: selectedProblem.value,
        note: comments,
        lumperCode,
        email: {
          subject: `*Resolved* ${selectedProblem?.value} #${load.billOfLadingNumber}`,
          body: createEmailBody,
          isBodyHtml: true,
          attachments: null,
        },
      };
      setLoading(true);
      resolveLoadProblem({
        ...payload,
        loadId: load.loadId,
        salesOrderBk: load.billOfLadingNumber,
      }).then(() => {
        showSuccessMessage(`Problem resolved on load #${load.billOfLadingNumber}`);
        resetState();
        refetchLoad({ problems: load.problems.filter((p) => p !== payload.problem) });
      }).catch((error) => {
        logger.error(formatErrorResponse(error));
        if (error?.response?.data?.Message?.includes('Problem does not exist')) {
          showErrorMessage('The problem is still being logged in the system. Please try resolving it in a few moments.');
        } else {
          showErrorMessage('Something went wrong. The service is currently unavailable. Please try again later.');
        }
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  /**
   * Handles when lumper code changes
   * @param {object} event - Change event
   * @return {void}
   */
  const handleLumperCodeChange = (event) => {
    setLumperCode(event.target.value);
  };

  if (loading) {
    return (
      <div data-testid="Update Status Action">
        <FormHeader>Resolve a Problem</FormHeader>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </div>
    );
  }

  // Check if load does not have any problems
  if (!load || !load.problems || load.problems.length === 0) {
    return (
      <EmptyStateContainer data-testid="Resolve Problem Action">
        There are no problems to resolve.
      </EmptyStateContainer>
    );
  }

  // Check if valid on every render
  const valid = checkValid();
  return (
    <div data-testid="Resolve Problem Action">
      <FormHeader>Resolve a Problem</FormHeader>
      <TopRowContainer>
        <ProblemInputContainer>
          <TextFieldLabel text="*Problem on Load" />
          <Select
            selectedValue={selectedProblem}
            options={load.problems.map((option) => ({ label: option, value: option }))}
            placeholder="Select an existing problem to resolve"
            onValueChange={handleSelectedProblemChanged}
          />
        </ProblemInputContainer>
        {selectedProblem?.value === 'Lumper Reimbursement' && (
        <ProblemInputContainer>
          <TextFieldLabel text="*Lumper Funds Code" />
          <TextField
            name="lumperCode"
            placeholder="Enter Code"
            value={lumperCode}
            onValueChange={handleLumperCodeChange}
            autoComplete="off"
          />
        </ProblemInputContainer>
        )}

      </TopRowContainer>
      <CommentBox
        comment={comments}
        onCommentChanged={handleCommentsChanged}
        placeholder="Include helpful details related to the solution."
        characterLimit={COMMENTS_CHARACTER_LIMIT}
        required
      />
      <SubmitButtonContainer>
        <Button onClick={handleSubmit} text="Submit" isDisabled={!valid} />
      </SubmitButtonContainer>
    </div>
  );
};

ResolveProblemAction.propTypes = {
  load: loadShape.isRequired,
  refetchLoad: func,
};

ResolveProblemAction.defaultProps = {
  refetchLoad: () => {},
};

export default ResolveProblemAction;
